import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';
import { debounce } from 'lodash-es';
import { Input, Paragraph, SearchIcon } from '@quickmd-llc/design-system';
import { useApplicationStore } from '@data';
import { fetchProvidersByStateAndVisitType, fetchProvidersAuthed } from '@api';
import { Events, tracking } from '@lib';
import { Provider } from '@types';
import SearchResult from './SearchResult';
import { getTimeZone, normalizeTextForSearch } from '@utils';
import { useFilters } from '../useFilters';

const debouncedEvent = debounce((value: string) => {
  tracking.trackEvent(Events.SEARCH_PROVIDERS, {
    Input: value,
  });
}, 500);

export default function ProviderSearch(): JSX.Element {
  const { visitTypeId } = useParams();
  const { stateCode, patientProfile, accessToken } = useApplicationStore(
    useShallow((state) => ({
      stateCode: state.stateCode,
      patientProfile: state.patient.patientProfile,
      accessToken: state.auth.accessToken,
    })),
  );
  const { setProvider } = useFilters();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchResults, setSearchResults] = useState<Provider[]>([]);

  const showSearchResults = Boolean(searchResults.length);
  const showEmptyState = Boolean(searchValue.trim() && !showSearchResults);

  useEffect(() => {
    async function fetchProviders() {
      if (!stateCode || !visitTypeId) {
        return;
      }

      try {
        const isLoggedIn = Boolean(accessToken && patientProfile);
        const providersRes = isLoggedIn
          ? await fetchProvidersAuthed(visitTypeId, getTimeZone())
          : await fetchProvidersByStateAndVisitType(stateCode, visitTypeId);

        setProviders(providersRes);
      } catch (error) {
        // @TODO: Handle error
        console.log(error);
      }
    }

    fetchProviders();
  }, [stateCode, visitTypeId, patientProfile, accessToken]);

  useEffect(() => {
    if (!searchValue.trim()) {
      setSearchResults([]);
      return;
    }

    const normalizedSearchValue = normalizeTextForSearch(searchValue);

    const results = providers.filter((provider) => {
      const providerFullName = `${provider.firstName} ${provider.lastName}`;
      const normalizedProviderName = normalizeTextForSearch(providerFullName);

      return normalizedProviderName.includes(normalizedSearchValue);
    });

    setSearchResults(results);
  }, [providers, searchValue]);

  function handleChange({
    currentTarget,
  }: React.ChangeEvent<HTMLInputElement>) {
    setSearchValue(currentTarget.value);
    debouncedEvent(currentTarget.value);
  }

  function handleClick(provider: Provider) {
    tracking.trackEvent(Events.SELECT_PROVIDER, {
      Provider: provider.id,
    });

    setProvider(provider);
  }

  return (
    <div className="relative">
      <Paragraph className="mb-2 font-semibold text-text-dark">
        Search providers
      </Paragraph>

      <Input
        placeholder="Search first and last names..."
        onChange={handleChange}
        rightSideLabel={<SearchIcon className="text-text-dark" />}
      />

      {showSearchResults && (
        <div className="absolute z-10 mt-1 w-full rounded-lg bg-white p-2 shadow-lg">
          {searchResults.map((provider) => (
            <SearchResult
              key={provider.id}
              provider={provider}
              onClick={handleClick}
            />
          ))}
        </div>
      )}

      {showEmptyState && (
        <div className="absolute z-10 mt-1 w-full rounded-lg bg-white p-6 shadow-lg">
          <Paragraph size="sm" className="text-center">
            No providers found
          </Paragraph>
        </div>
      )}
    </div>
  );
}
